import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  WBBox,
  WBButton,
  WBFlex,
  WBTypography,
  WBSplitButton,
} from '@admiin-com/ds-web';
import { useMediaQuery, useTheme } from '@mui/material';
import { BillCreateFormData } from '../../../../providers/TaskCreationProvider/TaskCreationProvider';
import { TaxReviewTable } from './TaxReviewTable';
import { TaxReviewMobile } from './TaxReviewMobile';
import { SendTemplateOption } from '../../../../../components/ESignature/SignatureForm/SendTemplateOption';
import { useWatch, useFormContext } from 'react-hook-form';
import { DocumentType } from '@admiin-com/ds-graphql';
import { TaxEmailMessage } from './TaxEmailMessage';
import { useTaskCreationContext } from '../../../../providers/TaskCreationProvider/useTaskCreationContext';

export const TaxReview = ({
  taxes,
  onEdit,
  onComplete,
  sending,
  onSaveAsDraft,
  onSaveForReview,
  saveAsDraftLoading,
  sendTemplate,
  onSendTemplateChange,
}: {
  taxes: BillCreateFormData[];
  onEdit?: (index: number) => void;
  onComplete: () => void;
  sending?: boolean;
  onSaveAsDraft?: () => void;
  onSaveForReview?: () => void;
  saveAsDraftLoading?: boolean;
  sendTemplate: boolean;
  onSendTemplateChange?: (
    checked: boolean,
    templateJson?: string,
    templateHtml?: string | null,
    includeLetterhead?: boolean
  ) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setValue } = useFormContext<any>();
  const { isTaxBinding } = useTaskCreationContext();
  const handleEditTax = (index: number) => {
    if (onEdit) {
      onEdit(index);
    }
  };
  const documentType = useWatch({ name: 'documentType' });
  const showSendTemplateOption = isTaxBinding;

  React.useEffect(() => {
    const defaultTo = taxes?.[0]?.to;
    if (defaultTo) {
      setValue('to', defaultTo);
      const shouldSendSms = defaultTo?.taskSendSMS === false ? false : true;
      setValue('sendSMS', shouldSendSms);
    } else {
      setValue('sendSMS', true);
    }
    setValue('tasks' as any, taxes ?? []);
  }, [taxes, setValue]);

  const sendBindingSms = useWatch({ name: 'sendSMS' });
  const primaryRecipient = taxes?.[0]?.to;

  if (!taxes || taxes.length === 0) {
    return (
      <WBBox p={4} textAlign="center">
        <WBTypography variant="h6" color="text.secondary">
          {t('noTaxDocumentsToReview', { ns: 'taskbox' })}
        </WBTypography>
      </WBBox>
    );
  }

  return (
    <WBBox p={3}>
      {/* Header */}
      <WBBox mb={3}>
        <WBTypography variant="h4" gutterBottom>
          {t('taxDocumentReview', { ns: 'taskbox' })}
        </WBTypography>
        <WBTypography variant="body1" color="text.secondary">
          {t('reviewTaxDocumentsBeforeSubmission', { ns: 'taskbox' })}
        </WBTypography>
      </WBBox>

      {/* Responsive Document List */}
      {isMobile ? (
        <TaxReviewMobile taxes={taxes} onEdit={handleEditTax} />
      ) : (
        <TaxReviewTable taxes={taxes} onEdit={handleEditTax} />
      )}

      {/* Email Message Section */}
      <WBBox
        mt={3}
        mb={3}
        p={3}
        border={1}
        borderColor="primary.main"
        borderRadius={1}
      >
        <TaxEmailMessage />
      </WBBox>

      {/*<WBBox mt={3} mb={3}>*/}
      {/*  <SMSNumber*/}
      {/*    onAdd={() => undefined}*/}
      {/*    contact={primaryRecipient}*/}
      {/*    checked={Boolean(sendBindingSms ?? true)}*/}
      {/*    disabled={primaryRecipient?.taskSendSMS === false}*/}
      {/*    allowSelection={false}*/}
      {/*  />*/}
      {/*</WBBox>*/}

      {/* Action Buttons */}
      <WBFlex
        gap={2}
        flexDirection={['column', 'row']}
        justifyContent={['center', 'space-between']}
      >
        {showSendTemplateOption && (
          <SendTemplateOption
            type={documentType === DocumentType.NONE ? documentType : 'BINDING'}
            checked={sendTemplate}
            onChange={(
              checked,
              templateJson,
              templateHtml,
              includeLetterhead
            ) =>
              onSendTemplateChange?.(
                checked,
                templateJson,
                templateHtml,
                includeLetterhead
              )
            }
          />
        )}
        <WBFlex gap={2} justifyContent={['center', 'flex-end']}>
          <WBSplitButton
            mainAction={{
              label: t('saveForReview', { ns: 'taskbox' }),
              onClick: () => onSaveForReview?.(),
            }}
            options={[
              {
                label: t('saveForReview', { ns: 'taskbox' }),
                onClick: () => onSaveForReview?.(),
              },
              {
                label: t('saveAsDraft', { ns: 'taskbox' }),
                onClick: () => onSaveAsDraft?.(),
              },
            ]}
            disabled={sending}
            loading={saveAsDraftLoading}
          />
          <WBButton
            variant="contained"
            color="primary"
            onClick={onComplete}
            loading={sending}
            disabled={saveAsDraftLoading}
          >
            {t('confirmAndSend', { ns: 'taskbox' })}
          </WBButton>
        </WBFlex>
      </WBFlex>
    </WBBox>
  );
};
